import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ScanLine,
  ClipboardList,
  Activity,
  Database,
  UsersRound,
  LogOut,
  ShieldCheck,
  Building2,
  Layers,
  Tag,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const USER_NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, testId: "nav-overview" },
  { to: "/analyze", label: "New Analysis", icon: ScanLine, testId: "nav-analyze" },
  { to: "/batch", label: "Batch Upload", icon: Layers, testId: "nav-batch" },
  { to: "/history", label: "History", icon: ClipboardList, testId: "nav-history" },
];

const ADMIN_NAV = [
  { to: "/export", label: "Export & Retrain", icon: Database, testId: "nav-export" },
  { to: "/admin/users", label: "User Management", icon: UsersRound, testId: "nav-admin-users" },
  { to: "/admin/classes", label: "Class Labels", icon: Tag, testId: "nav-admin-classes" },
];

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin, logout } = useAuth();

  const doLogout = async () => {
    await logout();
    toast.success("Logged out");
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 grain-bg">
      <div className="grid grid-cols-1 xl:grid-cols-[260px_1fr]">
        {/* Sidebar */}
        <aside className="xl:h-screen xl:sticky xl:top-0 border-b xl:border-b-0 xl:border-r border-zinc-900 bg-zinc-950/70 backdrop-blur-xl flex flex-col">
          <div className="px-6 py-6 flex items-center gap-3">
            <div className="relative w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
              <Activity className="w-5 h-5 text-white" strokeWidth={2.25} />
            </div>
            <div>
              <div className="font-display text-lg font-semibold tracking-tight leading-none">
                Thorax<span className="text-indigo-400">.AI</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mt-1">
                MCU Second Opinion
              </div>
            </div>
          </div>

          <nav className="px-3 py-2 space-y-1 flex-1 overflow-y-auto">
            <div className="text-[9px] uppercase tracking-[0.16em] text-zinc-600 font-semibold px-4 py-2">
              Workspace
            </div>
            {USER_NAV.map((item) => (
              <NavItem key={item.to} item={item} pathname={location.pathname} />
            ))}
            {isAdmin && (
              <>
                <div className="text-[9px] uppercase tracking-[0.16em] text-zinc-600 font-semibold px-4 pt-4 pb-2 flex items-center gap-1.5">
                  <ShieldCheck className="w-3 h-3" />
                  Admin
                </div>
                {ADMIN_NAV.map((item) => (
                  <NavItem key={item.to} item={item} pathname={location.pathname} />
                ))}
              </>
            )}
          </nav>

          {/* User card */}
          {user && (
            <div className="px-4 py-4 border-t border-zinc-900 space-y-3">
              <div
                data-testid="sidebar-user-card"
                className="rounded-lg bg-zinc-900/60 border border-zinc-800 p-3"
              >
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-sm font-medium text-zinc-100 truncate">
                    {user.name || user.email}
                  </div>
                  <span
                    className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                      isAdmin
                        ? "bg-indigo-500/20 text-indigo-300"
                        : "bg-zinc-800 text-zinc-400"
                    }`}
                  >
                    {isAdmin ? "admin" : "user"}
                  </span>
                </div>
                {user.clinic_name && (
                  <div className="text-[11px] text-zinc-500 truncate flex items-center gap-1">
                    <Building2 className="w-3 h-3" />
                    {user.clinic_name}
                  </div>
                )}
              </div>
              <button
                onClick={doLogout}
                data-testid="logout-btn"
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900/50 hover:bg-zinc-900 text-zinc-400 hover:text-zinc-100 px-3 py-2 text-xs font-medium transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" />
                Logout
              </button>
            </div>
          )}
        </aside>

        {/* Main */}
        <main className="min-h-screen">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function NavItem({ item, pathname }) {
  const Icon = item.icon;
  const active =
    item.to === "/"
      ? pathname === "/"
      : pathname.startsWith(item.to);
  return (
    <NavLink
      to={item.to}
      data-testid={item.testId}
      className={`group flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-all ${
        active
          ? "bg-indigo-500/10 text-indigo-300 border border-indigo-500/20"
          : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 border border-transparent"
      }`}
    >
      <Icon className="w-4 h-4" strokeWidth={1.75} />
      <span className="font-medium tracking-tight">{item.label}</span>
    </NavLink>
  );
}
